import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ApiClient } from './apiClient';
import { useApiClient } from './useApiClient';
import { useLocation } from '@/providers/LocationProvider';
import { useUiConfig } from './config';
import { calculateDistance, convertDistanceToMeters } from '@/utils/distance';

const PROFILES_BASE = '/profiles';

export interface ProfileLocation {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp?: number;
}

export interface ProfilePicture {
  url: string;
  source: 'auth0' | 'upload' | 'other';
}

export interface Profile {
  id: string;
  shortid?: string;
  name: string;
  email?: string;
  dateOfBirth: string;
  location: ProfileLocation;
  metadata: Record<string, unknown>;
  interests: string[];
  pictures?: ProfilePicture[];
  description?: string;
  searchDescription?: string;
  deleted?: boolean;
  preferredDistanceUnit?: 'ft' | 'm';
  themeType?: string;
  enableLocationBasedTheme?: boolean;
}

export interface ProfileListResponse {
  profiles: Profile[];
  offset: number;
  limit: number;
  count: number;
}

export interface NearbyProfilesRequest {
  profile: Pick<Profile, 'id' | 'location' | 'searchDescription'>;
  distance?: number;
  unitOfMeasure?: 'm' | 'km' | 'mi' | 'ft';
}

export interface NearbyLikeRequest extends NearbyProfilesRequest {
  query?: string;
  limit?: number;
}

export type SelfProfileUpdatePayload = Partial<
  Pick<
    Profile,
    | 'name'
    | 'email'
    | 'dateOfBirth'
    | 'description'
    | 'metadata'
    | 'interests'
    | 'pictures'
    | 'preferredDistanceUnit'
    | 'themeType'
    | 'enableLocationBasedTheme'
    | 'location'
  >
>;

const getSelfProfileRequest = async (client: ApiClient) =>
  client.get<Profile>(`${PROFILES_BASE}/me`);

const upsertSelfProfileRequest = async (
  client: ApiClient,
  profile: SelfProfileUpdatePayload,
) =>
  client.post<Profile>(`${PROFILES_BASE}/me`, {
    body: profile,
  });

export const toSelfProfileUpdatePayload = (
  profile: Partial<Profile>,
): SelfProfileUpdatePayload => {
  const payload: SelfProfileUpdatePayload = {};

  if (typeof profile.name === 'string') {
    payload.name = profile.name;
  }

  if (typeof profile.email === 'string') {
    payload.email = profile.email;
  }

  if (typeof profile.dateOfBirth === 'string') {
    payload.dateOfBirth = profile.dateOfBirth;
  }

  if (profile.description !== undefined) {
    payload.description = profile.description ?? '';
  }

  if (profile.metadata && typeof profile.metadata === 'object') {
    payload.metadata = profile.metadata;
  }

  if (Array.isArray(profile.interests)) {
    payload.interests = profile.interests;
  }

  if (Array.isArray(profile.pictures)) {
    payload.pictures = profile.pictures;
  }

  if (profile.preferredDistanceUnit) {
    payload.preferredDistanceUnit = profile.preferredDistanceUnit;
  }

  if (profile.themeType) {
    payload.themeType = profile.themeType;
  }

  if (typeof profile.enableLocationBasedTheme === 'boolean') {
    payload.enableLocationBasedTheme = profile.enableLocationBasedTheme;
  }

  if (profile.location) {
    payload.location = profile.location;
  }

  return payload;
};

const listProfilesRequest = async (
  client: ApiClient,
  params: { offset?: number; limit?: number; includeDeleted?: boolean },
) =>
  client.get<ProfileListResponse>(`${PROFILES_BASE}/list`, {
    searchParams: {
      offset: params.offset ?? 0,
      limit: params.limit ?? 100,
      includeDeleted: params.includeDeleted ?? false,
    },
  });

const getProfileRequest = async (client: ApiClient, id: string) =>
  client.get<Profile>(`${PROFILES_BASE}/${id}`);

const getProfileByShortIdRequest = async (client: ApiClient, shortid: string) =>
  client.get<Profile>(`${PROFILES_BASE}/shortid/${shortid}`);

const searchProfilesRequest = async (client: ApiClient, query: string, limit = 20) =>
  client.get<Profile[]>(`${PROFILES_BASE}/search`, {
    searchParams: { query, limit },
  });

const nearbyProfilesRequest = async (client: ApiClient, payload: NearbyProfilesRequest) =>
  client.post<Profile[]>(`${PROFILES_BASE}/nearby`, {
    body: payload,
  });

const nearbyLikeProfilesRequest = async (client: ApiClient, payload: NearbyLikeRequest) =>
  client.post<Profile[]>(`${PROFILES_BASE}/nearby/like`, {
    body: payload,
  });

const createProfileRequest = async (client: ApiClient, profile: Profile) =>
  client.post<Profile>(PROFILES_BASE, { body: profile });

const updateProfileRequest = async (client: ApiClient, profile: Profile) =>
  client.put<Profile>(PROFILES_BASE, { body: profile });

const deleteProfileRequest = async (client: ApiClient, id: string) =>
  client.delete<Profile>(`${PROFILES_BASE}/${id}`);

export const useProfilesList = (params: {
  offset?: number;
  limit?: number;
  includeDeleted?: boolean;
}) => {
  const client = useApiClient();

  return useQuery({
    queryKey: ['profiles', 'list', params],
    queryFn: () => listProfilesRequest(client, params),
  });
};

export const useProfile = (id: string, options: { enabled?: boolean } = {}) => {
  const client = useApiClient();

  return useQuery({
    queryKey: ['profiles', id],
    queryFn: () => getProfileRequest(client, id),
    enabled: options.enabled ?? Boolean(id),
  });
};

export const useProfileByShortId = (
  shortId: string,
  options: { enabled?: boolean } = {},
) => {
  const client = useApiClient();

  return useQuery({
    queryKey: ['profiles', 'shortid', shortId],
    queryFn: () => getProfileByShortIdRequest(client, shortId),
    enabled: options.enabled ?? Boolean(shortId),
  });
};

export const useProfileSearch = (query: string, limit = 20) => {
  const client = useApiClient();

  return useQuery({
    queryKey: ['profiles', 'search', query, limit],
    queryFn: () => searchProfilesRequest(client, query, limit),
    enabled: query.trim().length > 0,
  });
};

export const useSelfProfile = (options: { enabled?: boolean } = {}) => {
  const client = useApiClient();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['profiles', 'me'],
    queryFn: () => getSelfProfileRequest(client),
    enabled: options.enabled ?? true,
  });

  // Handle success side effect using useEffect
  useEffect(() => {
    const profile = query.data as Profile;
    if (profile?.id) {
      queryClient.setQueryData(['profiles', profile.id], profile);
    }
  }, [query.data, queryClient]);

  return query;
};

export const useUpsertSelfProfile = () => {
  const client = useApiClient();
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['profiles'] });

  return useMutation({
    mutationFn: (profile: SelfProfileUpdatePayload) =>
      upsertSelfProfileRequest(client, profile),
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData(['profiles', 'me'], updatedProfile);
      if (updatedProfile?.id) {
        queryClient.setQueryData(['profiles', updatedProfile.id], updatedProfile);
      }
      invalidate();
    },
  });
};

export const useNearbyProfiles = (payload: NearbyProfilesRequest) => {
  const client = useApiClient();

  return useQuery({
    queryKey: ['profiles', 'nearby', payload],
    queryFn: () => nearbyProfilesRequest(client, payload),
    enabled: Boolean(payload.profile?.location),
  });
};

export const useNearbyLikeProfiles = (payload: NearbyLikeRequest) => {
  const client = useApiClient();
  const location = useLocation();
  const { data: config } = useUiConfig();
  const upsertSelfProfile = useUpsertSelfProfile();

  return useQuery({
    queryKey: ['profiles', 'nearby-like', payload],
    queryFn: async () => {
      const finalPayload = { ...payload };
      const freshnessThreshold = config?.location?.freshnessThreshold ?? 30000;
      const movedSignificantlyDistance =
        config?.user?.movedSignificantly?.distance ?? 100;
      const movedSignificantlyUnit = config?.user?.movedSignificantly?.unit ?? 'm';
      const distanceThresholdMeters = convertDistanceToMeters(
        movedSignificantlyDistance,
        movedSignificantlyUnit,
      );
      const now = Date.now();

      let coordsToUse = location.coords;
      let shouldUpdateProfile = false;

      if (!coordsToUse || now - coordsToUse.timestamp > freshnessThreshold) {
        if (location.forceLocationUpdate) {
          try {
            await location.forceLocationUpdate();
            coordsToUse = location.coords;
          } catch (error) {
            console.warn(
              '[useNearbyLikeProfiles] forceLocationUpdate failed, using cached coords',
              error,
            );
          }
        }
      }

      if (coordsToUse) {
        const newLocation = {
          latitude: coordsToUse.latitude,
          longitude: coordsToUse.longitude,
          accuracy: coordsToUse.accuracy,
          timestamp: coordsToUse.timestamp,
        };

        finalPayload.profile = {
          ...finalPayload.profile,
          location: newLocation,
        };

        // Check if location has meaningfully changed (distance moved beyond threshold)
        const currentLocation = payload.profile.location;
        const distance = calculateDistance(
          currentLocation.latitude,
          currentLocation.longitude,
          newLocation.latitude,
          newLocation.longitude,
        );

        if (distance > distanceThresholdMeters && payload.profile.id) {
          shouldUpdateProfile = true;
        }
      }

      // Update profile location if it has meaningfully changed
      if (shouldUpdateProfile) {
        try {
          upsertSelfProfile.mutate(
            toSelfProfileUpdatePayload({
              location: finalPayload.profile.location,
            }),
          );
        } catch (error) {
          console.warn(
            '[useNearbyLikeProfiles] Failed to update profile location',
            error,
          );
        }
      }

      return nearbyLikeProfilesRequest(client, finalPayload);
    },
    enabled: Boolean(payload.profile?.location),
  });
};

export const useProfileMutations = () => {
  const client = useApiClient();
  const queryClient = useQueryClient();

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['profiles'] });

  const create = useMutation({
    mutationFn: (profile: Profile) => createProfileRequest(client, profile),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: (profile: Profile) => updateProfileRequest(client, profile),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteProfileRequest(client, id),
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
